import { create } from 'zustand'
import { ApiError } from '@/lib/api'
import { subscriptionApi } from '@/features/subscription/subscription.api'
import type { Subscription, SubscriptionPendingStatus, UserSession } from '@/types/models'

/** The session's plan, preloaded at boot for landlords. */
interface SubscriptionState {
  active: Subscription | null
  pending: SubscriptionPendingStatus | null
  loaded: boolean
  /** Never throws: a missing subscription or a failed call leaves the fields null. */
  loadForUser: (user: UserSession) => Promise<void>
  refreshActive: () => Promise<void>
  clear: () => void
}

export const useSubscriptionStore = create<SubscriptionState>((set) => ({
  active: null,
  pending: null,
  loaded: false,
  loadForUser: async (user) => {
    if (user.defaultProfile !== 'landlord') {
      set({ active: null, pending: null, loaded: true })
      return
    }
    const [active, pending] = await Promise.allSettled([
      subscriptionApi.active(),
      user.pendingSubscriptionId ? subscriptionApi.pendingStatus(user.pendingSubscriptionId) : Promise.resolve(null),
    ])
    const activeValue = active.status === 'fulfilled' ? active.value : null
    if (active.status === 'rejected' && !(active.reason instanceof ApiError && active.reason.status === 404)) {
      console.warn('subscription preload failed', active.reason)
    }
    const pendingValue = pending.status === 'fulfilled' && pending.value && pending.value.status !== 'active' ? pending.value : null
    set({ active: activeValue, pending: pendingValue, loaded: true })
  },
  refreshActive: async () => {
    try {
      set({ active: await subscriptionApi.active() })
    } catch (e) {
      if (!(e instanceof ApiError && e.status === 404)) console.warn('subscription refresh failed', e)
      set({ active: null })
    }
  },
  clear: () => set({ active: null, pending: null, loaded: false }),
}))
